'use client';

import { useEffect, useState } from 'react';

import cx from 'classnames';
import { useReducedMotion } from 'framer-motion';

import { ButtonVariant } from '~/ui/components/button';
import { PlayPauseButton } from '~/ui/components/button/playPause';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Breakpoint } from '~/ui/styles/grid';

import { CbaImageColumn } from './cbaImageColumn';
import styles from './cbaImageColumns.module.scss';
import { type CbaImagesCollection, type HiglightedTag } from './cbaScrollFeature';

interface CbaImageColumnsProps {
  cbaImages: CbaImagesCollection;
  highlightedTag?: HiglightedTag;
  className?: string;
}

// divides images up into each column one at a time and in order
const splitArrayToColumns = <T,>(inputArray: T[], numColumns: number) => {
  const columnArray: T[][] = [];
  for (let i = 0; i < numColumns; i++) {
    columnArray.push([]);
  }
  let j = 0;
  for (let i = 0; i < inputArray.length; i++) {
    const element = inputArray[i];
    columnArray[j].push(element);
    if (j < columnArray.length - 1) {
      j += 1;
    } else {
      j = 0;
    }
  }

  return columnArray;
};

const responsiveColumns = {
  [Breakpoint.MOBILE]: 5,
  [Breakpoint.TABLET]: 7,
  [Breakpoint.LAPTOP]: 7,
  [Breakpoint.DESKTOP]: 9,
};

export const CbaImageColumns = ({ cbaImages, highlightedTag, className }: CbaImageColumnsProps) => {
  const prefersReducedMotion = useReducedMotion();
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const breakpoint = useBreakpoint();

  const NUM_COLUMNS = breakpoint ? responsiveColumns[breakpoint] : 9;

  const columns = [];
  if (cbaImages) {
    const splitImages = splitArrayToColumns(cbaImages?.items, NUM_COLUMNS);
    for (let index = 0; index < NUM_COLUMNS; index++) {
      const element = (
        <CbaImageColumn
          images={splitImages[index]}
          key={index}
          direction={index % 2 === 0 ? 'normal' : 'reverse'}
          highlightedTag={highlightedTag}
          isPaused={isAnimationPaused}
        />
      );
      columns.push(element);
    }
  }
  useEffect(() => {
    if (prefersReducedMotion) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsAnimationPaused(true);
    }
  }, [prefersReducedMotion]);

  const handlePlayPauseClicked = () => {
    setIsAnimationPaused(!isAnimationPaused);
  };

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.columnsWrapper}>{columns}</div>
      <div className={cx(styles.controls)}>
        <PlayPauseButton
          isPaused={isAnimationPaused}
          onClick={handlePlayPauseClicked}
          className={styles.controlButton}
          variant={ButtonVariant.PRIMARY}
        />
      </div>
    </div>
  );
};
